/**
 * Lift Evaluation Form (LEF) — «Carry Deck Lift form».
 *
 * Ключи категорий такелажа и источников веса — зеркало констант LiftEvaluation
 * на бэке. Менять их нельзя: по ним лежат данные уже отправленных форм.
 */

export const RIGGING_CATEGORIES = [
    // «+» стоит на бланке только у строп и скоб: «No "+" on lifting / Rigging
    // hooks / Load Block / Spreader bar» — у остальных четырёх строка одна.
    { key: 'slings', label: 'Type of Slings', qtyLabel: '# of slings', multiRow: true },
    { key: 'shackles', label: 'Shackles', qtyLabel: '# of shackles', multiRow: true },
    { key: 'lifting_rings_eyebolts', label: 'Lifting Rings / Eyebolts', qtyLabel: 'Qty' },
    { key: 'rigging_hooks', label: 'Rigging Hooks', qtyLabel: 'Qty' },
    { key: 'load_block_jib', label: 'Load Block / Jib', qtyLabel: 'Qty' },
    { key: 'spreader_bars', label: 'Spreader Bars Below the Hook Lifting Devices', qtyLabel: 'Qty' },
];

/**
 * Чем определили вес груза. На бумаге весь блок «HOW WEIGHT OF OBJECT OBTAINED»
 * заменён одним выпадающим списком и единственным полем веса.
 */
export const WEIGHT_SOURCES = [
    { key: 'certified_weight_scale', label: 'Certified Weight Scale' },
    { key: 'drawing', label: 'Drawing' },
    { key: 'calculation', label: 'Calculation' },
    { key: 'estimation', label: 'Estimation' },
];

export const GROUND_TYPE = {
    SOIL: 'soil',
    CONCRETE: 'concrete',
};

export const EQUIPMENT_SOURCE = {
    INTERNAL: 'internal',
    EXTERNAL: 'external',
};

/**
 * «Person requesting the lift» — человек, который просит выполнить подъём.
 * По умолчанию тот, кто заполняет форму на планшете.
 */
export const defaultRequestedBy = () => {
    try {
        return localStorage.getItem('employeeFullName') || '';
    } catch (e) {
        return '';
    }
};

/**
 * Краны в справочнике техники идут с префиксом «CR ». Форма сделана под них:
 * все 11 единиц с этим префиксом проходят годовую инспекцию, форклифты — нет.
 */
export const CRANE_CODE_PREFIX = 'CR ';

export const ASME_NOTE =
    'Must comply with ASME B30.20 standard for design, testing and appropriate markings — 200% proof test.';

/** Больше двенадцати фото на один подъём бэк не примет. */
export const MAX_PHOTOS = 12;

/** Столько же получателей принимает сервер — держим числа в одном месте. */
export const MAX_RECIPIENTS = 20;
export const MAX_EMAIL_LENGTH = 254;

/** Адрес проверяем перед добавлением: список уходит письмами с PDF во вложении. */
export const isValidEmail = (value) => {
    const email = String(value || '').trim();
    if (!email || email.length > MAX_EMAIL_LENGTH) return false;
    // Одна собака, непустые части, точка в домене, без пробелов.
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
};

export const emptyRiggingRow = (category) => ({
    category,
    description: '',
    qty: '',
    ratedCapacity: '',
});

/** Пустая таблица такелажа: по одной строке на категорию, как на бумаге. */
export const emptyRiggingItems = () => RIGGING_CATEGORIES.map((c) => emptyRiggingRow(c.key));

/**
 * Приводит таблицу такелажа к виду бумажной формы: каждая категория присутствует,
 * пустые строки в ней восстанавливаются.
 *
 * Сервер не хранит незаполненные строки, поэтому в черновике с сервера и в копии
 * предыдущего подъёма приходят только заполненные. Без этого категория без строк
 * рисовалась бы одним заголовком, и вписать в неё что-либо было бы нечем.
 */
export const normalizeRiggingItems = (items) => {
    const source = Array.isArray(items) ? items : [];

    return RIGGING_CATEGORIES.flatMap((category) => {
        const rows = source.filter((item) => item?.category === category.key);
        return rows.length ? rows : [emptyRiggingRow(category.key)];
    });
};

const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const cleaned = String(value).replace(/[,\s]/g, '');
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
};

/** A + B. Возвращает null, пока не заполнено ни одно слагаемое. */
export const calcTotalWeight = (objectWeight, riggingWeight) => {
    const a = toNumber(objectWeight);
    const b = toNumber(riggingWeight);
    if (a === null && b === null) return null;
    return (a || 0) + (b || 0);
};

export const parseNumber = toNumber;

/**
 * Годовая инспекция действует год. Просрочку считаем на дату подъёма,
 * а не на сегодня: форму часто заполняют заранее.
 */
export const isInspectionOverdue = (inspectionDate, liftDate) => {
    if (!inspectionDate) return false;
    const inspection = new Date(inspectionDate);
    if (Number.isNaN(inspection.getTime())) return false;

    const reference = liftDate ? new Date(liftDate) : new Date();
    if (Number.isNaN(reference.getTime())) return false;

    const dueBy = new Date(inspection);
    dueBy.setFullYear(dueBy.getFullYear() + 1);

    return dueBy < reference;
};

export const formatWeight = (value) => {
    const n = toNumber(value);
    return n === null ? '—' : `${n.toLocaleString('en-US', { maximumFractionDigits: 0 })} lbs`;
};

/**
 * Что НЕ переносится в копию на следующий день.
 *
 * Подписи — под конкретным подъёмом конкретного дня, их ставят заново.
 * Фотографии расстановки — доказательство того дня, за ночь площадка меняется.
 * Служебные поля отправки к новому черновику отношения не имеют.
 */
const NOT_COPIED = [
    'id', 'uuid', 'status', 'createdAt', 'updatedAt',
    'submittedAt', 'submittedByName', 'copiedFromUuid', 'weightsConfirmed',
    'supervisorSignature', 'supervisorSignedAt',
    'riggerSignature', 'riggerSignedAt',
    'operatorSignature', 'operatorSignedAt',
    'customerSignature', 'customerSignedAt',
    'safetySignature', 'safetySignedAt',
    'liftingOperationPhotos',
    'annualInspectionOverdue',
];

/**
 * Черновик на следующий день из отправленной формы.
 * Веса переносятся как есть, но помечаются неподтверждёнными: пока человек
 * их не подтвердит, форма не отправится (это же проверяет и сервер).
 *
 * @param {object} source полная форма с сервера
 * @param {{uuid: string, liftDate: string}} next
 */
export const buildCopy = (source, { uuid, liftDate }) => {
    const copy = {};
    for (const [key, value] of Object.entries(source || {})) {
        if (!NOT_COPIED.includes(key)) copy[key] = value;
    }

    return {
        ...copy,
        uuid,
        liftDate,
        riggingItems: normalizeRiggingItems(copy.riggingItems),
        liftingOperationPhotos: [],
        supervisorSignature: '',
        riggerSignature: '',
        operatorSignature: '',
        customerSignature: '',
        safetySignature: '',
        copiedFromUuid: source?.uuid || null,
        weightsConfirmed: false,
        status: 'draft',
    };
};
